import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { normalizeLanguageCode, isSupportedLanguage } from '@/lib/languageUtils';

// Only English is statically imported — always available without async loading.
// All other languages are lazy-loaded on demand via dynamic import().
import enTranslation from '../locales/en/translation.json';
import enCommon from '../locales/en/common.json';
import enDashboard from '../locales/en/dashboard.json';
import enCCTVDesigner from '../locales/en/cctv-designer.json';
import enValidation from '../locales/en/validation.json';
import enCalculators from '../locales/en/calculators.json';
import enRodo from '../locales/en/rodo.json';
import enLens from '../locales/en/lens.json';

// Lazy loaders for all non-English languages (code-split per language)
const languageLoaders: Record<string, Record<string, () => Promise<any>>> = {
  pl: {
    translation: () => import('../locales/pl/translation.json'),
    common: () => import('../locales/pl/common.json'),
    dashboard: () => import('../locales/pl/dashboard.json'),
    'cctv-designer': () => import('../locales/pl/cctv-designer.json'),
    validation: () => import('../locales/pl/validation.json'),
    calculators: () => import('../locales/pl/calculators.json'),
    rodo: () => import('../locales/pl/rodo.json'),
    lens: () => import('../locales/pl/lens.json'),
  },
  de: {
    translation: () => import('../locales/de/translation.json'),
    common: () => import('../locales/de/common.json'),
    dashboard: () => import('../locales/de/dashboard.json'),
    'cctv-designer': () => import('../locales/de/cctv-designer.json'),
    validation: () => import('../locales/de/validation.json'),
    calculators: () => import('../locales/de/calculators.json'),
    rodo: () => import('../locales/de/rodo.json'),
    lens: () => import('../locales/de/lens.json'),
  },
  'pt-br': {
    translation: () => import('../locales/pt-br/translation.json'),
    common: () => import('../locales/pt-br/common.json'),
    dashboard: () => import('../locales/pt-br/dashboard.json'),
    'cctv-designer': () => import('../locales/pt-br/cctv-designer.json'),
    validation: () => import('../locales/pt-br/validation.json'),
    calculators: () => import('../locales/pt-br/calculators.json'),
    rodo: () => import('../locales/pt-br/rodo.json'),
    lens: () => import('../locales/pt-br/lens.json'),
  },
  'pt-pt': {
    translation: () => import('../locales/pt-pt/translation.json'),
    common: () => import('../locales/pt-pt/common.json'),
    dashboard: () => import('../locales/pt-pt/dashboard.json'),
    'cctv-designer': () => import('../locales/pt-pt/cctv-designer.json'),
    validation: () => import('../locales/pt-pt/validation.json'),
    calculators: () => import('../locales/pt-pt/calculators.json'),
    rodo: () => import('../locales/pt-pt/rodo.json'),
    lens: () => import('../locales/pt-pt/lens.json'),
  },
  es: {
    translation: () => import('../locales/es/translation.json'),
    common: () => import('../locales/es/common.json'),
    dashboard: () => import('../locales/es/dashboard.json'),
    'cctv-designer': () => import('../locales/es/cctv-designer.json'),
    validation: () => import('../locales/es/validation.json'),
    calculators: () => import('../locales/es/calculators.json'),
    rodo: () => import('../locales/es/rodo.json'),
    lens: () => import('../locales/es/lens.json'),
  },
  hi: {
    translation: () => import('../locales/hi/translation.json'),
    common: () => import('../locales/hi/common.json'),
    dashboard: () => import('../locales/hi/dashboard.json'),
    'cctv-designer': () => import('../locales/hi/cctv-designer.json'),
    validation: () => import('../locales/hi/validation.json'),
    calculators: () => import('../locales/hi/calculators.json'),
    rodo: () => import('../locales/hi/rodo.json'),
    lens: () => import('../locales/hi/lens.json'),
  },
  id: {
    translation: () => import('../locales/id/translation.json'),
    common: () => import('../locales/id/common.json'),
    dashboard: () => import('../locales/id/dashboard.json'),
    'cctv-designer': () => import('../locales/id/cctv-designer.json'),
    validation: () => import('../locales/id/validation.json'),
    calculators: () => import('../locales/id/calculators.json'),
    rodo: () => import('../locales/id/rodo.json'),
    lens: () => import('../locales/id/lens.json'),
  },
  fil: {
    translation: () => import('../locales/fil/translation.json'),
    common: () => import('../locales/fil/common.json'),
    dashboard: () => import('../locales/fil/dashboard.json'),
    'cctv-designer': () => import('../locales/fil/cctv-designer.json'),
    validation: () => import('../locales/fil/validation.json'),
    calculators: () => import('../locales/fil/calculators.json'),
    rodo: () => import('../locales/fil/rodo.json'),
    lens: () => import('../locales/fil/lens.json'),
  },
  tr: {
    translation: () => import('../locales/tr/translation.json'),
    common: () => import('../locales/tr/common.json'),
    dashboard: () => import('../locales/tr/dashboard.json'),
    'cctv-designer': () => import('../locales/tr/cctv-designer.json'),
    validation: () => import('../locales/tr/validation.json'),
    calculators: () => import('../locales/tr/calculators.json'),
    rodo: () => import('../locales/tr/rodo.json'),
    lens: () => import('../locales/tr/lens.json'),
  },
  ar: {
    translation: () => import('../locales/ar/translation.json'),
    common: () => import('../locales/ar/common.json'),
    dashboard: () => import('../locales/ar/dashboard.json'),
    'cctv-designer': () => import('../locales/ar/cctv-designer.json'),
    validation: () => import('../locales/ar/validation.json'),
    calculators: () => import('../locales/ar/calculators.json'),
    rodo: () => import('../locales/ar/rodo.json'),
    lens: () => import('../locales/ar/lens.json'),
  },
  vi: {
    translation: () => import('../locales/vi/translation.json'),
    common: () => import('../locales/vi/common.json'),
    dashboard: () => import('../locales/vi/dashboard.json'),
    'cctv-designer': () => import('../locales/vi/cctv-designer.json'),
    validation: () => import('../locales/vi/validation.json'),
    calculators: () => import('../locales/vi/calculators.json'),
    rodo: () => import('../locales/vi/rodo.json'),
    lens: () => import('../locales/vi/lens.json'),
  },
  th: {
    translation: () => import('../locales/th/translation.json'),
    common: () => import('../locales/th/common.json'),
    dashboard: () => import('../locales/th/dashboard.json'),
    'cctv-designer': () => import('../locales/th/cctv-designer.json'),
    validation: () => import('../locales/th/validation.json'),
    calculators: () => import('../locales/th/calculators.json'),
    rodo: () => import('../locales/th/rodo.json'),
    lens: () => import('../locales/th/lens.json'),
  },
  ja: {
    translation: () => import('../locales/ja/translation.json'),
    common: () => import('../locales/ja/common.json'),
    dashboard: () => import('../locales/ja/dashboard.json'),
    'cctv-designer': () => import('../locales/ja/cctv-designer.json'),
    validation: () => import('../locales/ja/validation.json'),
    calculators: () => import('../locales/ja/calculators.json'),
    rodo: () => import('../locales/ja/rodo.json'),
    lens: () => import('../locales/ja/lens.json'),
  },
  ko: {
    translation: () => import('../locales/ko/translation.json'),
    common: () => import('../locales/ko/common.json'),
    dashboard: () => import('../locales/ko/dashboard.json'),
    'cctv-designer': () => import('../locales/ko/cctv-designer.json'),
    validation: () => import('../locales/ko/validation.json'),
    calculators: () => import('../locales/ko/calculators.json'),
    rodo: () => import('../locales/ko/rodo.json'),
    lens: () => import('../locales/ko/lens.json'),
  },
  fr: {
    translation: () => import('../locales/fr/translation.json'),
    common: () => import('../locales/fr/common.json'),
    dashboard: () => import('../locales/fr/dashboard.json'),
    'cctv-designer': () => import('../locales/fr/cctv-designer.json'),
    validation: () => import('../locales/fr/validation.json'),
    calculators: () => import('../locales/fr/calculators.json'),
    rodo: () => import('../locales/fr/rodo.json'),
    lens: () => import('../locales/fr/lens.json'),
  },
  it: {
    translation: () => import('../locales/it/translation.json'),
    common: () => import('../locales/it/common.json'),
    dashboard: () => import('../locales/it/dashboard.json'),
    'cctv-designer': () => import('../locales/it/cctv-designer.json'),
    validation: () => import('../locales/it/validation.json'),
    calculators: () => import('../locales/it/calculators.json'),
    rodo: () => import('../locales/it/rodo.json'),
    lens: () => import('../locales/it/lens.json'),
  },
  uk: {
    translation: () => import('../locales/uk/translation.json'),
    common: () => import('../locales/uk/common.json'),
    dashboard: () => import('../locales/uk/dashboard.json'),
    'cctv-designer': () => import('../locales/uk/cctv-designer.json'),
    validation: () => import('../locales/uk/validation.json'),
    calculators: () => import('../locales/uk/calculators.json'),
    rodo: () => import('../locales/uk/rodo.json'),
    lens: () => import('../locales/uk/lens.json'),
  },
  cs: {
    translation: () => import('../locales/cs/translation.json'),
    common: () => import('../locales/cs/common.json'),
    dashboard: () => import('../locales/cs/dashboard.json'),
    'cctv-designer': () => import('../locales/cs/cctv-designer.json'),
    validation: () => import('../locales/cs/validation.json'),
    calculators: () => import('../locales/cs/calculators.json'),
    rodo: () => import('../locales/cs/rodo.json'),
    lens: () => import('../locales/cs/lens.json'),
  },
  nl: {
    translation: () => import('../locales/nl/translation.json'),
    common: () => import('../locales/nl/common.json'),
    dashboard: () => import('../locales/nl/dashboard.json'),
    'cctv-designer': () => import('../locales/nl/cctv-designer.json'),
    validation: () => import('../locales/nl/validation.json'),
    calculators: () => import('../locales/nl/calculators.json'),
    rodo: () => import('../locales/nl/rodo.json'),
    lens: () => import('../locales/nl/lens.json'),
  },
  hu: {
    translation: () => import('../locales/hu/translation.json'),
    common: () => import('../locales/hu/common.json'),
    dashboard: () => import('../locales/hu/dashboard.json'),
    'cctv-designer': () => import('../locales/hu/cctv-designer.json'),
    validation: () => import('../locales/hu/validation.json'),
    calculators: () => import('../locales/hu/calculators.json'),
    rodo: () => import('../locales/hu/rodo.json'),
    lens: () => import('../locales/hu/lens.json'),
  },
  lt: {
    translation: () => import('../locales/lt/translation.json'),
    common: () => import('../locales/lt/common.json'),
    dashboard: () => import('../locales/lt/dashboard.json'),
    'cctv-designer': () => import('../locales/lt/cctv-designer.json'),
    validation: () => import('../locales/lt/validation.json'),
    calculators: () => import('../locales/lt/calculators.json'),
    rodo: () => import('../locales/lt/rodo.json'),
    lens: () => import('../locales/lt/lens.json'),
  },
};

const enResources = {
  en: {
    translation: enTranslation,
    common: enCommon,
    dashboard: enDashboard,
    'cctv-designer': enCCTVDesigner,
    validation: enValidation,
    calculators: enCalculators,
    rodo: enRodo,
    lens: enLens,
  },
};

// Detect language from the current URL path (for hydration matching)
function getUrlLanguage(): string {
  if (typeof window === 'undefined') return 'en';
  const segments = window.location.pathname.split('/').filter(Boolean);
  const LANG_PREFIXES = ['pl', 'de', 'es', 'fil', 'hi', 'id', 'pt-br', 'pt-pt', 'tr', 'ar', 'vi', 'th', 'ja', 'ko', 'fr', 'it', 'uk', 'cs', 'nl', 'hu', 'lt'];
  return (segments[0] && LANG_PREFIXES.includes(segments[0])) ? segments[0] : 'en';
}

// Get saved language from localStorage or default to English
const getSavedLanguage = (): string => {
  if (import.meta.env.SSR) return 'en';
  try {
    const saved = localStorage.getItem('i18n_language');
    if (saved) {
      const normalized = normalizeLanguageCode(saved);
      if (normalized && isSupportedLanguage(normalized)) {
        return normalized;
      }
    }
  } catch (e) {
    console.error('Error reading language preference:', e);
  }
  return 'en';
};

// Initialize i18next with English only. Other languages are loaded on demand.
// During SSG and client hydration, the correct language is loaded before rendering
// via the async callback in main.tsx.
const initLang = import.meta.env.SSR ? 'en' : getUrlLanguage();

i18n
  .use(initReactI18next)
  .init({
    resources: enResources,
    fallbackLng: 'en',
    defaultNS: 'translation',
    ns: ['translation', 'common', 'dashboard', 'cctv-designer', 'validation', 'calculators', 'rodo', 'lens'],
    lng: initLang,
    interpolation: {
      escapeValue: false,
    },
    lowerCaseLng: true,
    supportedLngs: ['en', 'pl', 'de', 'pt-br', 'pt-pt', 'es', 'hi', 'id', 'fil', 'tr', 'ar', 'vi', 'th', 'ja', 'ko', 'fr', 'it', 'uk', 'cs', 'nl', 'hu', 'lt'],
    fallbackNS: 'translation',
  });

// Load a language on demand — fetches all missing namespaces via dynamic import
export const loadLanguage = async (lang: string): Promise<void> => {
  if (lang === 'en') return; // English is always available

  const loaders = languageLoaders[lang];
  if (!loaders) return;

  // Find namespaces that aren't loaded yet
  const missing = Object.entries(loaders).filter(
    ([ns]) => !i18n.hasResourceBundle(lang, ns)
  );

  if (missing.length === 0) return;

  try {
    const results = await Promise.all(
      missing.map(async ([ns, loader]) => {
        const module = await loader();
        return [ns, module.default || module] as const;
      })
    );
    for (const [ns, data] of results) {
      i18n.addResourceBundle(lang, ns, data, true, true);
    }
  } catch (error) {
    console.error(`Error loading language ${lang}:`, error);
  }
};

// Call this AFTER hydration (e.g. in a useEffect) to restore the user's saved language
export const restoreSavedLanguage = async (): Promise<void> => {
  const savedLang = getSavedLanguage();
  if (savedLang !== 'en') {
    await loadLanguage(savedLang);
    await i18n.changeLanguage(savedLang);
  }
};

/**
 * Extract language from a URL path and set i18n to that language SYNCHRONOUSLY.
 * Used during SSG and client hydration after resources have been loaded.
 *
 * IMPORTANT: call loadLanguage(lang) first to ensure resources are available.
 */
export const setLanguageFromPath = (pathname: string): string => {
  const LANG_PREFIXES = ['pl', 'de', 'es', 'fil', 'hi', 'id', 'pt-br', 'pt-pt', 'tr', 'ar', 'vi', 'th', 'ja', 'ko', 'fr', 'it', 'uk', 'cs', 'nl', 'hu', 'lt'];
  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0];
  const lang = (firstSegment && LANG_PREFIXES.includes(firstSegment)) ? firstSegment : 'en';

  // Synchronous language switch — sets internal state directly.
  i18n.language = lang;
  // @ts-expect-error i18next internal API
  i18n.languages = [lang, 'en'];
  // @ts-expect-error i18next internal API
  i18n.resolvedLanguage = lang;

  i18n.emit('languageChanged', lang);

  return lang;
};

export default i18n;
